import { useEffect, useRef, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import './index.scss'

const PROMPT = 'touheed@portfolio ~ %'

const SECTIONS = {
  '': '/',
  '~': '/',
  '/': '/',
  about: '/about',
  projects: '/projects',
  skills: '/skills',
  experience: '/experience',
  contact: '/contact',
}

const WORK = [
  { company: 'Nagarro', role: 'Staff Engineer', duration: 'May 2022 – Present' },
  { company: 'Saint-Gobain', role: 'Tech Lead', duration: 'Oct 2017 – Apr 2022' },
  { company: 'Ebrandz', role: 'Software Developer', duration: 'Sep 2015 – Oct 2017' },
  { company: '69 Productions', role: 'Junior Developer', duration: 'Dec 2014 – Sep 2015' },
]

const PROJECTS = [
  { title: 'Project Assist', stack: 'Nuxt.js · Vue · AI · LLM · MCP' },
  { title: 'TOPS2', stack: 'Java · Spring Boot · Vue · Microservices' },
  { title: 'Connect My Partner (CMP)', stack: 'Java · Spring Boot · Angular 12' },
  { title: 'Telecom Tools Suite', stack: 'Java · Spring Boot · Angular 12' },
]

const SKILLS = [
  'Java', 'Spring Boot', 'Microservices', 'AI / LLM',
  'Angular', 'Vue.js', 'Docker', 'REST APIs',
]

const FILES = {
  'about.md':
    'Staff Engineer with 11+ years building enterprise-grade applications.\n' +
    'Java Full Stack · AI integration · technical leadership.',
  'skills.txt': SKILLS.join(' · '),
  'experience.log': WORK.map(
    (w) => `${w.duration.padEnd(22)} ${w.company} — ${w.role}`,
  ).join('\n'),
  'contact.vcf':
    'Touheed Khan\nStaff Engineer @ Nagarro\nEmail: touheed.khan.93@gmail.com',
}

const COMMANDS = [
  'help', 'whoami', 'about', 'skills', 'experience', 'work', 'projects',
  'contact', 'resume', 'cv', 'social', 'ls', 'cd', 'open', 'cat', 'pwd',
  'echo', 'date', 'neofetch', 'sudo', 'exit', 'clear', 'history', 'banner',
]

const WELCOME = [
  'Welcome to touheed.sh — an interactive portfolio terminal.',
  "Type 'help' to get started, or 'neofetch' for the highlight reel.",
]

const NEOFETCH = [
  '    _____         touheed@portfolio',
  '   |_   _|        ─────────────────',
  '     | |          OS:      PortfolioOS 11.0',
  '     | |          Host:    Nagarro · Staff Engineer',
  '     | |          Shell:   zsh',
  '     |_|          Uptime:  11+ years in the industry',
  '                  Stack:   Java · Spring · AI · Angular · Vue',
  '                  Email:   touheed.khan.93@gmail.com',
]

let lineId = 0
const nextId = () => ++lineId

const Terminal = ({ onClose }) => {
  const navigate = useNavigate()
  const [lines, setLines] = useState(() =>
    WELCOME.map((content) => ({ id: nextId(), type: 'out', content })),
  )
  const [input, setInput] = useState('')
  const [cmdHistory, setCmdHistory] = useState([])
  const [histIdx, setHistIdx] = useState(-1)

  const inputRef = useRef(null)
  const bodyRef = useRef(null)

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight
    }
  }, [lines])

  const print = (content) => {
    const arr = Array.isArray(content) ? content : [content]
    setLines((prev) => [
      ...prev,
      ...arr.map((c) => ({ id: nextId(), type: 'out', content: c })),
    ])
  }

  const runCommand = (raw) => {
    const trimmed = raw.trim()
    const [cmd, ...args] = trimmed.split(/\s+/)
    const arg = args.join(' ')

    setLines((prev) => [
      ...prev,
      { id: nextId(), type: 'cmd', content: trimmed },
    ])

    if (!cmd) return

    switch (cmd.toLowerCase()) {
      case 'help':
        print([
          'Available commands:',
          '  about       skills      experience   projects',
          '  whoami      contact     resume       social',
          '  ls          cd <dir>    cat <file>   neofetch',
          '  echo        pwd         date         history',
          '  clear       banner      help',
          "Tip: 'cd projects' actually navigates the site. Tab to autocomplete.",
        ])
        break
      case 'whoami':
        print('Touheed Khan — Staff Engineer @ Nagarro')
        break
      case 'about':
        print([
          FILES['about.md'],
          "→ try 'cd about' to open the full page.",
        ])
        break
      case 'skills':
        print(['Core skills:', '  ' + SKILLS.join('  ·  ')])
        break
      case 'experience':
      case 'work':
        print(['Work history:', ...WORK.map(
          (w) => `  ${w.duration.padEnd(22)} ${w.company} — ${w.role}`,
        )])
        break
      case 'projects':
        print(['Selected projects:', ...PROJECTS.map(
          (p, i) => `  ${i + 1}. ${p.title}  [${p.stack}]`,
        )])
        break
      case 'contact':
        print([
          'Email: touheed.khan.93@gmail.com',
          "→ try 'cd contact' to open the contact form.",
        ])
        break
      case 'resume':
      case 'cv':
        print("Head to the contact page to reach out — try 'cd contact'.")
        break
      case 'social':
        print([
          'GitHub:   https://github.com/touheedkhan',
          'LinkedIn: https://www.linkedin.com/in/touheedkhan',
        ])
        break
      case 'ls':
        print('about.md   projects/   skills.txt   experience.log   contact.vcf')
        break
      case 'cd':
      case 'open': {
        const key = arg.replace(/\/$/, '').toLowerCase()
        if (key in SECTIONS) {
          const path = SECTIONS[key]
          print(`navigating to ${path} …`)
          setTimeout(() => navigate(path), 350)
        } else {
          print(`cd: no such directory: ${arg}`)
        }
        break
      }
      case 'cat':
        if (FILES[arg]) print(FILES[arg].split('\n'))
        else if (!arg) print('usage: cat <file> — try `ls`')
        else print(`cat: ${arg}: No such file`)
        break
      case 'pwd':
        print('/home/touheed')
        break
      case 'echo':
        print(arg)
        break
      case 'date':
        print(new Date().toString())
        break
      case 'neofetch':
        print(NEOFETCH)
        break
      case 'sudo':
        print('Nice try 😏 — permission denied.')
        break
      case 'exit':
        if (onClose) {
          print('logout')
          setTimeout(() => onClose(), 250)
        } else {
          print("There's no escape from this portfolio 😄")
        }
        break
      case 'history':
        print(cmdHistory.length
          ? cmdHistory.map((c, i) => `  ${i + 1}  ${c}`)
          : 'No history yet.')
        break
      case 'banner':
        print(WELCOME)
        break
      case 'clear':
        setLines([])
        break
      default:
        print(`command not found: ${cmd} — type 'help'`)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const value = input
      if (value.trim()) {
        setCmdHistory((prev) => [...prev, value.trim()])
      }
      setHistIdx(-1)
      setInput('')
      runCommand(value)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (!cmdHistory.length) return
      const idx = histIdx === -1 ? cmdHistory.length - 1 : Math.max(0, histIdx - 1)
      setHistIdx(idx)
      setInput(cmdHistory[idx])
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (histIdx === -1) return
      const idx = histIdx + 1
      if (idx >= cmdHistory.length) {
        setHistIdx(-1)
        setInput('')
      } else {
        setHistIdx(idx)
        setInput(cmdHistory[idx])
      }
    } else if (e.key === 'Tab') {
      e.preventDefault()
      const word = input.trim()
      if (!word) return
      const match = COMMANDS.find((c) => c.startsWith(word.toLowerCase()))
      if (match) setInput(match)
    } else if (e.key === 'l' && e.ctrlKey) {
      e.preventDefault()
      setLines([])
    } else if (e.key === 'Escape' && onClose) {
      onClose()
    }
  }

  const focusInput = () => inputRef.current && inputRef.current.focus()

  return (
    <div className="terminal-window" onClick={focusInput}>
      <div className="window-bar">
        <span
          className={`dot dot-red${onClose ? ' dot-action' : ''}`}
          onClick={
            onClose
              ? (e) => {
                  e.stopPropagation()
                  onClose()
                }
              : undefined
          }
          onKeyDown={
            onClose
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onClose()
                  }
                }
              : undefined
          }
          role={onClose ? 'button' : undefined}
          tabIndex={onClose ? 0 : undefined}
          aria-label={onClose ? 'Close terminal' : undefined}
        />
        <span className="dot dot-yellow" />
        <span className="dot dot-green" />
        <span className="window-title">touheed — zsh</span>
      </div>
      <div className="terminal-body" ref={bodyRef}>
        {lines.map((line) =>
          line.type === 'cmd' ? (
            <div className="term-line term-cmd" key={line.id}>
              <span className="term-prompt">{PROMPT}</span>{' '}
              <span className="term-input-echo">{line.content}</span>
            </div>
          ) : (
            <div className="term-line term-out" key={line.id}>
              {line.content}
            </div>
          ),
        )}
        <div className="terminal-input-row">
          <span className="term-prompt">{PROMPT}</span>
          <input
            ref={inputRef}
            className="terminal-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
            aria-label="terminal command input"
          />
        </div>
      </div>
    </div>
  )
}

export default Terminal
